import { useNotyf } from "contexts/NotyfContext";
import useSWRMutation from "swr/mutation";
import { ApiError, isCancelledRequest, wrapApiError } from "../apiErrors";
import { client } from "../client";
import { resolveApiResourceUrl } from "../resources/routeUtils";
import type { ApiResourceRoute } from "../resources/types";
import { getRequestConfig } from "./queryConfig";
import type { ApiGetFunc } from "./types";

export function useApiGetAsync<TEntity, TResponse = TEntity>() {
  const notyf = useNotyf();
  const { trigger, isMutating, error, data, reset } = useSWRMutation<
    TResponse,
    unknown,
    Readonly<{ namespace: "mutationGet" }>,
    ApiResourceRoute<TEntity>
  >(
    { namespace: "mutationGet" },
    async (_key, { arg: resourceRoute }) => {
      const url = resolveApiResourceUrl(resourceRoute);
      if (!url) throw new Error("No API route provided");

      try {
        const result = await client().request<TResponse>({
          method: "GET",
          url,
          ...getRequestConfig(),
        });
        return result.data;
      } catch (error) {
        const apiError = wrapApiError(error);
        if (apiError instanceof ApiError && !isCancelledRequest(apiError)) {
          notyf.error(apiError.detail);
        }
        throw apiError;
      }
    },
    { populateCache: false, revalidate: false },
  );

  const onRequest: ApiGetFunc<TResponse, TEntity> = trigger;

  return {
    isLoading: isMutating,
    error,
    data,
    reset,
    onRequest,
  };
}
